import { Container, TrophyMark } from '@tt/design-system';
import { clsx } from 'clsx';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';

type SiteHeaderVariant = 'app' | 'public' | 'minimal';

type SiteHeaderProps = {
  variant?: SiteHeaderVariant;
  className?: string;
};

function maskEmail(email: string): string {
  if (email.length <= 16) return email;
  const [local, domain] = email.split('@');
  const head = local.slice(0, 3);
  return `${head}…@${domain}`;
}

export function SiteHeader({ variant = 'app', className }: SiteHeaderProps) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  function handleLogout() {
    logout();
    setMenuOpen(false);
    navigate('/login', { replace: true });
  }

  return (
    <header
      className={clsx(
        'sticky top-0 z-30 border-b border-gold/15 bg-night/85 backdrop-blur',
        className,
      )}
    >
      <Container size="wide" as="div" className="flex items-center justify-between py-4">
        <Link
          to={variant === 'minimal' ? '/' : user ? '/minha-colecao' : '/'}
          className="flex items-center gap-3 transition-opacity duration-fast hover:opacity-90"
          aria-label="Touch the Trophy — Início"
        >
          <TrophyMark variant="filled" size="sm" />
          <div className="hidden sm:block">
            <p className="font-display text-base text-parchment">Touch the Trophy</p>
            <p className="font-mono text-[10px] uppercase tracking-stamp text-champagne">
              Eternalize Tech
            </p>
          </div>
        </Link>

        {variant === 'app' && user ? (
          <div className="relative flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="group flex items-center gap-2 rounded-md border border-gold/20 bg-night/60 px-3 py-2 transition-colors duration-fast hover:border-gold/40"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold-gradient text-xs font-semibold text-night">
                {user.email[0].toUpperCase()}
              </span>
              <span className="hidden font-mono text-xs uppercase tracking-stamp text-champagne sm:inline">
                {maskEmail(user.email)}
              </span>
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 text-champagne transition-transform duration-fast group-hover:translate-y-0.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {menuOpen ? (
              <div
                role="menu"
                className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-md border border-gold/20 bg-night/95 shadow-md backdrop-blur"
              >
                <Link
                  to="/minha-colecao"
                  role="menuitem"
                  className="block px-4 py-3 text-sm transition-colors duration-fast hover:bg-gold/10"
                  onClick={() => setMenuOpen(false)}
                >
                  Minha coleção
                </Link>
                <Link
                  to="/gallery"
                  role="menuitem"
                  className="block px-4 py-3 text-sm transition-colors duration-fast hover:bg-gold/10"
                  onClick={() => setMenuOpen(false)}
                >
                  Galeria pública
                </Link>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full border-t border-gold/15 px-4 py-3 text-left text-sm text-champagne transition-colors duration-fast hover:bg-gold/10"
                  onClick={handleLogout}
                >
                  Sair
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {variant === 'public' ? (
          <Link
            to={user ? '/minha-colecao' : '/register'}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-gold/60 bg-transparent px-6 py-3 font-display text-base font-semibold tracking-wide text-gold transition-all duration-base ease-ceremonial hover:border-gold hover:bg-gold/5 hover:shadow-goldSoft focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold"
          >
            Quero estar na taça
          </Link>
        ) : null}
      </Container>
    </header>
  );
}
